import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaTrophy, FaChartLine, FaRedo, FaAward, FaStar, FaClock, FaCheck, FaTimes } from 'react-icons/fa';
import { GiSpeedometer, GiPencil } from 'react-icons/gi';
import { sendApiRequest } from '../utils/api';

const Results = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await sendApiRequest('api/tests/results/history', {}, 'GET');
        setResults(response);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch results');
        setLoading(false);
      }
    };
    fetchResults();
  }, []);

  const getPerformanceColor = (accuracy) => {
    if (accuracy >= 90) return 'from-green-500 to-emerald-600';
    if (accuracy >= 80) return 'from-blue-500 to-cyan-600';
    if (accuracy >= 70) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-pink-600';
  };

  const getPerformanceLevel = (accuracy) => {
    if (accuracy >= 90) return 'Excellent! 🎉';
    if (accuracy >= 80) return 'Great Job! 👍';
    if (accuracy >= 70) return 'Good Work! 👏';
    return 'Keep Practicing! 💪';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-700 font-medium">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTimes className="text-red-500 text-3xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg mb-4">
            <FaTrophy className="text-yellow-500 text-3xl" />
            <h1 className="text-3xl font-bold text-gray-800">My Learning Journey</h1>
            <FaChartLine className="text-blue-500 text-3xl" />
          </div>
          <p className="text-gray-600 text-lg">Track your progress and see how you're improving!</p>
        </div>

        {results.length > 0 ? (
          <div className="space-y-6">
            {results.map((result, index) => (
              <div 
                key={result._id} 
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left: Performance Overview */}
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <FaBook className="text-blue-600 text-xl" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-800">
                              {result.test?.title || 'Practice Exercise'}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                              <FaClock className="text-xs" />
                              {formatDate(result.date || result.completedAt)}
                            </div>
                          </div>
                        </div>
                        
                        {result.test?.exam?.name && (
                          <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm font-medium">
                            {result.test.exam.name}
                          </span>
                        )}
                      </div>

                      {/* Performance Rating */}
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg font-bold text-gray-800">
                            {getPerformanceLevel(result.accuracy)}
                          </span>
                          <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${getPerformanceColor(result.accuracy)}`}></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          You achieved {result.accuracy}% accuracy in this exercise
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                        <div className="text-center p-3 bg-green-50 rounded-xl border border-green-100">
                          <div className="text-2xl font-bold text-green-700">{result.details?.correct ?? 0}</div>
                          <div className="text-xs text-green-600 font-medium">Correct</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-xl border border-red-100">
                          <div className="text-2xl font-bold text-red-700">{result.details?.mistake ?? 0}</div>
                          <div className="text-xs text-red-600 font-medium">Mistakes</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-xl border border-yellow-100">
                          <div className="text-2xl font-bold text-yellow-700">{result.details?.missing ?? 0}</div>
                          <div className="text-xs text-yellow-600 font-medium">Missing</div>
                        </div>
                      </div>

                      {/* Additional Metrics */}
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-full">
                          <GiSpeedometer className="text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">{result.wordsPerMinute} WPM</span>
                        </div>
                        <div className="flex items-center gap-2 bg-purple-50 px-3 py-1 rounded-full">
                          <GiPencil className="text-purple-600" />
                          <span className="text-sm font-medium text-purple-700">{result.mistakes} Total Errors</span>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-full">
                          <FaClock className="text-gray-600" />
                          <span className="text-sm font-medium text-gray-700">{Math.floor(result.timeTaken / 60)}m {result.timeTaken % 60}s</span>
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions & Audio */}
                    <div className="lg:w-48 space-y-4">
                      {/* Accuracy Badge */}
                      <div className="text-center">
                        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${getPerformanceColor(result.accuracy)} text-white font-bold text-lg`}>
                          {result.accuracy}%
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Accuracy</div>
                      </div>

                      {/* Audio Player */}
                      {/* {result.audioUrl && (
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs text-gray-600 mb-2 text-center">Listen Again</p>
                          <audio 
                            controls 
                            src={result.audioUrl}
                            className="w-full rounded-lg"
                            preload="metadata"
                          />
                        </div>
                      )} */}

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            navigate(`/exam-result/${result.test?._id || 'unknown'}`, {
                              state: {
                                stats: {
                                  correct: result.details?.correct,
                                  mistake: result.details?.mistake,
                                  missing: result.details?.missing,
                                  totalMistake: result.details?.totalMistake || result.mistakes,
                                  accuracy: result.accuracy,
                                  dictationSpeed: result.wordsPerMinute,
                                  totalWords: result.details?.refWords?.length || result.details?.totalWords,
                                  typedWords: result.details?.userWords?.length || result.details?.typedWords,
                                  time: result.timeTaken,
                                  date: new Date(result.date || result.completedAt).toLocaleDateString(),
                                  punctuation: result.punctuation || result.details?.punctuation || 0,
                                  userWords: result.details?.userWords,
                                  refWords: result.details?.refWords,
                                  comparison: result.details?.comparison || 0,
                                  detailedPenalties: result.details?.detailedPenalties || [],
                                  selectedRule: result.selectedRule || { name: 'Standard' },
                                },
                                userInput: result.typedText,
                                reference: result.referenceText,
                                audioUrl: result.audioUrl,
                                topicName: result.test?.title || 'Practice Exercise',
                              },
                            });
                          }}
                          className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white w-full py-2 rounded-lg font-medium transition-colors text-sm"
                        >
                          <FaAward /> View Details
                        </button>
                        
                        <button
                          onClick={() => navigate('/dashboard')}
                          className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-600 text-white w-full py-2 rounded-lg font-medium transition-colors text-sm"
                        >
                          <FaRedo /> Practice More
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Indicator */}
                <div className="h-1 bg-gray-200">
                  <div 
                    className={`h-full bg-gradient-to-r ${getPerformanceColor(result.accuracy)}`}
                    style={{ width: `${result.accuracy}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaStar className="text-blue-500 text-3xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No Results Yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              You haven't completed any exercises yet. Start practicing to see your progress here!
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <FaBook /> Start Practicing
            </button>
          </div>
        )}

        {/* Summary Stats */}
        {results.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg border border-blue-100">
              <div className="text-3xl font-bold text-blue-600 mb-2">{results.length}</div>
              <div className="text-gray-600 font-medium">Total Exercises</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg border border-blue-100">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {Math.round(results.reduce((sum, result) => sum + result.accuracy, 0) / results.length)}%
              </div>
              <div className="text-gray-600 font-medium">Average Accuracy</div>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-lg border border-blue-100">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {Math.round(results.reduce((sum, result) => sum + result.wordsPerMinute, 0) / results.length)}
              </div>
              <div className="text-gray-600 font-medium">Average WPM</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Results;